import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanText } from "@domain/project/floorplan/floorplan-text";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanKvmReceiver } from "@domain/project/floorplan/monitoring/floorplan-kvm-receiver";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { GroupChipKonva } from "@project/floorplanner/konva/group-chip-konva";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";

export class IconKonva {
  private static readonly LABEL_FONT_SIZE = 5;

  static init(
    element:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanText
      | FloorplanGasWarningCenterPlaceholder,
    icon: Konva.Image,
    warningSignIcon?: Konva.Image,
    productGroup?: Konva.Group,
    dragMoveCallback?: (icon: Konva.Image) => void,
    transformCallback?: (icon: Konva.Image) => void,
  ) {
    const iconGroup = this.initIconGroup(element);
    this.initIconAttrs(icon, element);
    this.initItemText(icon, element, iconGroup, productGroup, dragMoveCallback, transformCallback);
    this.initTransformEndHandler(icon, element);
    iconGroup.add(icon);
    if (warningSignIcon) {
      this.attachWarningSign(icon, warningSignIcon, iconGroup);
    }
    return iconGroup;
  }

  static addDragMoveHandler(icon: Konva.Image, dragMoveCallback?: (icon: Konva.Image) => void, textGroup?: Konva.Group) {
    icon.on("dragmove", () => {
      if (textGroup) {
        this.setTextGroupPosition(textGroup, icon);
      }
      if (dragMoveCallback) {
        dragMoveCallback(icon);
      }
    });
  }

  static initIconAttrs(
    icon: Konva.Image,
    element:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanText
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanRemoteSensor
      | FloorplanKvmReceiver,
    xOffset: number = 0,
    yOffset: number = 0,
  ) {
    return icon.setAttrs({
      image: icon.image(),
      x: xOffset + element.x,
      y: yOffset + element.y,
      scaleX: element.size,
      scaleY: element.size,
      fontFamily: "Times New Roman",
      draggable: false,
      [FloorplanWorkspaceKonvaAdapter.OBJECT_KEY]: element,
    });
  }

  static initIconGroup(
    element:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanText
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanRemoteSensor
      | FloorplanKvmReceiver,
  ) {
    const iconGroup = new Group({
      id: element.id,
    });
    iconGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, element);
    return iconGroup;
  }

  static initTransformEndHandler(
    icon: Konva.Image,
    element:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanText
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanRemoteSensor
      | FloorplanKvmReceiver,
  ) {
    icon.on("transformend", () => {
      element.updateSize(icon.scaleX(), icon.x(), icon.y());
    });
  }

  static refreshItemText(productItem: FloorplanItem | FloorplanProductItem<ProductConfiguration> | FloorplanText, group: Group) {
    const textGroup = group.findOne(".itemText") as Group;
    if (!textGroup) {
      return;
    }
    const itemText = FloorplanItemTextKonva.getText(textGroup);
    const groupChip = textGroup.findOne(".groupChip") as Group | null;

    FloorplanItemTextKonva.refreshText(productItem, textGroup);
    this.positionGroupChip(textGroup, itemText, groupChip ?? undefined);
    const iconNode = group.getChildren()[1];
    if (iconNode instanceof Konva.Image) {
      this.setTextGroupPosition(textGroup, iconNode);
    }
  }

  static refreshWarningSignVisibility(iconGroup: Group, visible: boolean) {
    const warningSign = iconGroup ? iconGroup.getChildren()[2] : undefined;
    if (warningSign) {
      warningSign.visible(visible);
    }
  }

  static setPosition(
    icon: Konva.Image,
    x: number,
    y: number,
    size: number,
    xOffsetFactor: number = 0,
    yOffsetFactor: number = 0,
  ) {
    const xOffset = xOffsetFactor * size;
    const yOffset = yOffsetFactor * size;
    icon.x(x + xOffset);
    icon.y(y + yOffset);
  }

  private static initItemText(
    icon: Konva.Image,
    element:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanFlameDetectorPlaceholder
      | FloorplanProductItem<ProductConfiguration>
      | FloorplanText
      | FloorplanGasWarningCenterPlaceholder,
    iconGroup: Group,
    productGroup?: Konva.Group,
    dragMoveCallback?: (icon: Konva.Image) => void,
    transformCallback?: (icon: Konva.Image) => void,
  ) {
    const textGroup = FloorplanItemTextKonva.init(element, this.LABEL_FONT_SIZE);
    const itemText = FloorplanItemTextKonva.getText(textGroup);
    this.scaleTextGroup(textGroup, icon);
    FloorplanItemTextKonva.updateBackground(textGroup);
    this.setTextGroupPosition(textGroup, icon);
    iconGroup.add(textGroup);

    if (productGroup) {
      GroupChipKonva.init(productGroup, textGroup.width()).subscribe((groupChip: Konva.Group) => {
        if (groupChip) {
          textGroup.add(groupChip);
          this.positionGroupChip(textGroup, itemText, groupChip);
        }
      });
    }

    this.initTransformHandler(icon, textGroup, transformCallback);
    this.addDragMoveHandler(icon, dragMoveCallback, textGroup);
  }

  private static initTransformHandler(
    icon: Konva.Image,
    textGroup: Konva.Group,
    transformCallback?: (icon: Konva.Image) => void,
  ) {
    icon.on("transform", () => {
      this.scaleTextGroup(textGroup, icon);
      FloorplanItemTextKonva.updateBackground(textGroup);
      const itemText = FloorplanItemTextKonva.getText(textGroup);
      const groupChip = textGroup.findOne(".groupChip") as Group | null;
      this.positionGroupChip(textGroup, itemText, groupChip ?? undefined);
      this.setTextGroupPosition(textGroup, icon);
      if (transformCallback) transformCallback(icon);
    });
  }

  private static setTextGroupPosition(textGroup: Konva.Group, image: Konva.Image) {
    const item =
      image.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY) ??
      image.getParent()?.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(textGroup, image, item);
  }

  private static scaleTextGroup(textGroup: Konva.Group, icon: Konva.Image) {
    textGroup!.scaleX(icon.scaleX());
    textGroup!.scaleY(icon.scaleY());
  }

  private static positionGroupChip(textGroup: Group, itemText: Konva.Text, groupChip?: Group) {
    if (!groupChip) {
      return;
    }
    const backgroundGroup = textGroup.findOne(".itemTextBackground") as Konva.Group | null;
    const backgroundFill = backgroundGroup?.findOne("Rect") as Konva.Rect | null;
    if (!backgroundGroup || !backgroundFill) {
      return;
    }
    GroupChipKonva.setPosition(groupChip, itemText.x() + itemText.width() + 1, backgroundGroup.y());
    FloorplanItemTextKonva.updateBackground(textGroup);
    const chipBounds = groupChip.getClientRect({ relativeTo: textGroup });
    const yOffset = backgroundGroup.y() + (backgroundFill.height() - chipBounds.height) / 2;
    GroupChipKonva.setPosition(groupChip, itemText.x() + itemText.width() + 1, yOffset);
    FloorplanItemTextKonva.updateBackground(textGroup);
  }

  private static attachWarningSign(icon: Konva.Image, warningSignIcon: Konva.Image, iconGroup: Group) {
    warningSignIcon.visible(false);
    this.setWarningSize(warningSignIcon, icon);
    this.setWarningPosition(warningSignIcon, icon);

    icon.on("transform", () => {
      this.setWarningSize(warningSignIcon, icon);
      this.setWarningPosition(warningSignIcon, icon);
    });
    icon.on("dragmove", () => {
      this.setWarningPosition(warningSignIcon, icon);
    });

    iconGroup.add(warningSignIcon);
  }

  private static setWarningPosition(warningIcon: Konva.Image, icon: Konva.Image) {
    warningIcon.x(icon.x() + icon.width() * icon.scaleX() - 0.9 * warningIcon.width() * warningIcon.scaleX());
    warningIcon.y(icon.y());
  }

  private static setWarningSize(warningIcon: Konva.Image, floorplanIcon: Konva.Image) {
    const scalingFactor: number = 0.3;
    warningIcon.scaleX(floorplanIcon.scaleX() * scalingFactor);
    warningIcon.scaleY(floorplanIcon.scaleY() * scalingFactor);
  }
}
